import { AutoComplete } from 'antd';
import styled from 'styled-components';
import { Circle } from '@phosphor-icons/react';
import { ActiveText, ColoredPill, MuteText } from '../../elements';
import { CommonUtility, theme } from '../../utility';
import {
  skuAutocompleteHook,
  useAutocompleteInput,
  useAutocompleteLoading,
  useDebounce,
} from '../../hooks';
import { ItemImage, AutocompleteSearchInput } from '../../components';

const OptionContainer = styled.div`
  padding: 8px 12px;
`;

const PackagingMaterialName = styled(ActiveText)`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

function resultRenderer({ search, _id, p_title, p_sku, state, p_images }) {
  const showResult = string => {
    try {
      const highlighted = string.replace(
        new RegExp(search, 'gi'),
        d => `<strong>${d}</strong>`,
      );

      return <span dangerouslySetInnerHTML={{ __html: highlighted }} />;
    } catch (error) {
      return string;
    }
  };

  return (
    <OptionContainer key={_id}>
      <div className="d-flex align-items-center">
        <ItemImage
          item={{ p_images }}
          imageKey="p_images"
          displayImageKey="display_image"
        />
        <div className="d-flex flex-column">
          <span>{p_title}</span>
          <div className="d-flex">
            <MuteText className="mr-2">SKU</MuteText>
            <PackagingMaterialName title={p_sku} className="mr-2">
              {showResult(p_sku)}
            </PackagingMaterialName>
          </div>
          <ColoredPill
            color={state === 'Component' ? theme.colors.darkBlue : theme.colors.orange}
          >
            <Circle weight="fill" />
            {CommonUtility.toTitleCase(state)}
          </ColoredPill>
        </div>
      </div>
    </OptionContainer>
  );
}

export function SkuSearchInput({
  top,
  onResultClick,
  setTableSearch = () => {},
  organization_id,
  disabled = false,
}) {
  const { display, query, onSearch, onSelect, clear } =
    useAutocompleteInput(setTableSearch);

  const finalSearch = useDebounce(query, 1000, 0);

  const { data, loading } = skuAutocompleteHook(organization_id, finalSearch);
  const isSearching = useAutocompleteLoading(query, finalSearch, loading);

  const optionIds = (data ?? []).map(d => d._id).filter(Boolean);

  const options = (data ?? []).map(item => ({
    value: item._id,
    label: resultRenderer({ ...item, search: display }),
  }));

  return (
    <AutoComplete
      style={{ width: '100%' }}
      value={display}
      options={options}
      filterOption={false}
      placeholder="Search SKU"
      disabled={disabled}
      placement={top ? 'topLeft' : 'bottomLeft'}
      classNames={{ popup: { root: 'auto-search-dropdown' } }}
      onSearch={value => onSearch(value, optionIds)}
      onSelect={value => {
        const item = data.find(d => d._id === value);
        if (item?._id) {
          onSelect(item.p_sku || item.p_title || '', () => onResultClick(item));
        }
      }}
    >
      <AutocompleteSearchInput
        isSearching={isSearching}
        disabled={disabled}
        onClear={clear}
      />
    </AutoComplete>
  );
}
